import { useEffect, useState } from 'react';
import { DATE_FORMAT, useDateService } from 'framework/utils';
import { useListContext } from 'framework/list';
import { DateInput, FormFieldsData } from 'framework/form';
import moment from 'moment';
import useAccountingCycle from '../../useAccountingCycle';
import { PERFORM_ASSET_COST_PRORATING } from 'domain/finance/assets/useAssetService';
import { ButtonProps } from 'framework/ui/mui-builder';
import { ButtonWithConfirmation } from 'framework/ui/ui-builder';

interface Props extends ButtonProps {
  initialValue?: string;
  disabledInput?: boolean;
}

const AccrueRevenueButton = ({ disabledInput = false, initialValue = '', ...props }: Props) => {
  const { accrueRevenue } = useAccountingCycle();
  const [dateErrors, setDateErrors] = useState<string[]>([]);
  const { getToday } = useDateService();
  const { filter, reloadData = () => {} } = useListContext();
  const currentFilterValue = filter?.period?.eq;
  const currentMonth = getToday().endOf('months');
  const [date, setDate] = useState(initialValue || currentFilterValue || currentMonth);

  const onDateChange = (values: FormFieldsData) => {
    setDate(values.date);
    const isValid = moment(values.date, DATE_FORMAT.API_ONLY_MONTH, true).isValid();

    if (!values.date) {
      setDateErrors(['Field is required']);
    } else if (!isValid) {
      setDateErrors(['Please, choose the correct date']);
    } else if (moment(values.date).isAfter(currentMonth)) {
      setDateErrors(["Please, choose a date before or today's date"]);
    } else {
      setDateErrors([]);
    }
  };

  useEffect(() => {
    if (currentFilterValue) {
      onDateChange({ date: currentFilterValue });
    }
    if (initialValue) {
      onDateChange({ date: initialValue });
    }
    // Suppressed warnings because we only need to call useEffect callback if currentFilterValue is changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilterValue, initialValue]);

  return (
    <ButtonWithConfirmation
      displayMessage="Do you really want to perform accrue revenue? Please, fill out the input below."
      modalChildren={
        <DateInput
          source="date"
          sx={{ mt: 2.5 }}
          label="Period"
          onChangeCallback={onDateChange}
          errorMessages={dateErrors}
          isRequired
          dateFormat={DATE_FORMAT.API_ONLY_MONTH}
          value={
            date && moment(date, DATE_FORMAT.API_ONLY_MONTH, true).isValid()
              ? moment(date).format(DATE_FORMAT.API_ONLY_MONTH)
              : (date as any)
          }
          maxDate={currentMonth}
          disabled={disabledInput}
        />
      }
      submitMethod={() => accrueRevenue(moment(date).format(DATE_FORMAT.API_ONLY_MONTH))}
      confirmBtnProps={{ disabled: !!dateErrors.length }}
      permissionToCheck={PERFORM_ASSET_COST_PRORATING}
      afterSubmit={{
        successMessage:
          'Performing of accrue revenue has been successfully started. Please, check this page after some time.',
        errorMessage: "Can't start performing of accrue revenue.",
        execute: () => {
          reloadData();
        },
      }}
      {...props}
    >
      Accrue revenue
    </ButtonWithConfirmation>
  );
};

export default AccrueRevenueButton;
